/**
 * Sensitivity Scenario Modal (SPRINT-25 F005)
 *
 * Add/Edit modal for sensitivity scenarios. Provides:
 * - Scenario Name (text, required)
 * - Scenario Type (select enum)
 * - Rate Change in bps (visible for rate and combined/stress types)
 * - Income Change % (visible for income and combined/stress types)
 * - Affected Entity (optional select — not fully wired, placeholder)
 * - Notes (textarea, optional)
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { DealSensitivityScenario } from "@shared/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ScenarioType =
  | "rate_increase"
  | "rate_decrease"
  | "income_reduction"
  | "income_increase"
  | "combined"
  | "stress_test";

const SCENARIO_TYPE_LABELS: Record<ScenarioType, string> = {
  rate_increase: "Rate Increase",
  rate_decrease: "Rate Decrease",
  income_reduction: "Income Reduction",
  income_increase: "Income Increase",
  combined: "Combined",
  stress_test: "Stress Test",
};

const RATE_TYPES: ScenarioType[] = ["rate_increase", "rate_decrease"];
const INCOME_TYPES: ScenarioType[] = ["income_reduction", "income_increase"];
const BOTH_TYPES: ScenarioType[] = ["combined", "stress_test"];

function showRateField(type: ScenarioType | ""): boolean {
  return RATE_TYPES.includes(type as ScenarioType) || BOTH_TYPES.includes(type as ScenarioType);
}

function showIncomeField(type: ScenarioType | ""): boolean {
  return INCOME_TYPES.includes(type as ScenarioType) || BOTH_TYPES.includes(type as ScenarioType);
}

interface SensitivityScenarioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  scenario: DealSensitivityScenario | null;
  onSaved?: () => void;
}

interface FormState {
  scenarioName: string;
  scenarioType: ScenarioType | "";
  rateChangeBps: string;
  incomeChangePercent: string;
  notes: string;
}

const DEFAULT_FORM: FormState = {
  scenarioName: "",
  scenarioType: "",
  rateChangeBps: "",
  incomeChangePercent: "",
  notes: "",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SensitivityScenarioModal({
  open,
  onOpenChange,
  dealId,
  scenario,
  onSaved,
}: SensitivityScenarioModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = scenario != null;

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);

  // Populate form when editing
  useEffect(() => {
    if (scenario) {
      setForm({
        scenarioName: scenario.scenarioName,
        scenarioType: scenario.scenarioType as ScenarioType,
        rateChangeBps: scenario.rateChangeBps != null ? String(scenario.rateChangeBps) : "",
        incomeChangePercent:
          scenario.incomeChangePercent != null
            ? String(parseFloat(scenario.incomeChangePercent as string))
            : "",
        notes: scenario.notes ?? "",
      });
    } else {
      setForm(DEFAULT_FORM);
    }
  }, [scenario, open]);

  const saveMutation = useMutation({
    mutationFn: async (data: FormState) => {
      const payload: Record<string, unknown> = {
        scenarioName: data.scenarioName.trim(),
        scenarioType: data.scenarioType,
        notes: data.notes.trim() || null,
      };

      if (showRateField(data.scenarioType)) {
        payload.rateChangeBps = data.rateChangeBps !== "" ? parseInt(data.rateChangeBps, 10) : null;
      } else {
        payload.rateChangeBps = null;
      }

      if (showIncomeField(data.scenarioType)) {
        payload.incomeChangePercent =
          data.incomeChangePercent !== "" ? data.incomeChangePercent : null;
      } else {
        payload.incomeChangePercent = null;
      }

      if (isEditing && scenario) {
        return apiRequest("PATCH", `/api/financial/deals/${dealId}/sensitivity-scenarios/${scenario.id}`, payload);
      }
      return apiRequest("POST", `/api/financial/deals/${dealId}/sensitivity-scenarios`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/sensitivity-scenarios`],
      });
      toast({
        title: isEditing ? "Scenario updated" : "Scenario created",
        description: isEditing
          ? "Sensitivity scenario has been updated."
          : "Sensitivity scenario has been created.",
      });
      onOpenChange(false);
      onSaved?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save scenario",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!form.scenarioName.trim()) {
      toast({ title: "Scenario name is required", variant: "destructive" });
      return;
    }
    if (!form.scenarioType) {
      toast({ title: "Scenario type is required", variant: "destructive" });
      return;
    }

    saveMutation.mutate(form);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="modal-sensitivity-scenario"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Sensitivity Scenario" : "Add Sensitivity Scenario"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Scenario Name */}
          <div className="space-y-1.5">
            <Label htmlFor="scenario-name">Scenario Name</Label>
            <Input
              id="scenario-name"
              data-testid="input-scenario-name"
              placeholder="e.g. +200bps Rate Stress"
              value={form.scenarioName}
              onChange={(e) => setForm((prev) => ({ ...prev, scenarioName: e.target.value }))}
              required
            />
          </div>

          {/* Scenario Type */}
          <div className="space-y-1.5">
            <Label htmlFor="scenario-type">Scenario Type</Label>
            <Select
              value={form.scenarioType}
              onValueChange={(val) =>
                setForm((prev) => ({ ...prev, scenarioType: val as ScenarioType }))
              }
            >
              <SelectTrigger
                id="scenario-type"
                data-testid="select-scenario-type"
              >
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(SCENARIO_TYPE_LABELS) as [ScenarioType, string][]).map(
                  ([value, label]) => (
                    <SelectItem
                      key={value}
                      value={value}
                      data-testid={`select-item-${value}`}
                    >
                      {label}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Rate Change (conditional) */}
          {showRateField(form.scenarioType) && (
            <div className="space-y-1.5">
              <Label htmlFor="rate-change-bps">Rate Change (basis points)</Label>
              <Input
                id="rate-change-bps"
                data-testid="input-rate-change-bps"
                type="number"
                placeholder="e.g. 200"
                value={form.rateChangeBps}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, rateChangeBps: e.target.value }))
                }
              />
            </div>
          )}

          {/* Income Change (conditional) */}
          {showIncomeField(form.scenarioType) && (
            <div className="space-y-1.5">
              <Label htmlFor="income-change-percent">Income Change (%)</Label>
              <Input
                id="income-change-percent"
                data-testid="input-income-change-percent"
                type="number"
                step="0.01"
                placeholder="e.g. -15 for -15%"
                value={form.incomeChangePercent}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, incomeChangePercent: e.target.value }))
                }
              />
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              data-testid="textarea-notes"
              placeholder="Additional context..."
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="button-save-scenario"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Scenario
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
